import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/shared/lib/api";
import { Card, CardContent, Badge, Button } from "@/shared/components/ui";
import {
  getStatusVariant,
  getStatusLabel,
  formatCurrency,
  formatDateTime,
  getCreditTypeLabel,
} from "@/shared/lib/utils";
import { MessageSquare, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import type { Session, Lead } from "@/shared/types";

export function SessionsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => api.getSessions(),
  });

  const sessions = (data?.sessions || []) as (Session & { lead?: Lead })[];

  // Sort sessions: active first, then by date
  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.status === "active" && b.status !== "active") return -1;
    if (a.status !== "active" && b.status === "active") return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać sesji</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sesje</h1>
        <p className="text-gray-600 mt-1">
          Zarządzaj swoimi sesjami z klientami
        </p>
      </div>

      {/* Sessions List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : sortedSessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Brak sesji</p>
            <p className="text-sm text-gray-500 mt-1">
              Zarezerwuj leada, aby rozpocząć nową sesję
            </p>
            <Link to="/leads" className="inline-block mt-4">
              <Button>Przeglądaj leady</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedSessions.map((session) => (
            <Card
              key={session.id}
              className={`hover:shadow-md transition-shadow ${
                session.status === "active" ? "border-primary-200 bg-primary-50/30" : ""
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {session.clientData?.name || "Klient"}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        {session.lead && (
                          <>
                            <span className="text-sm text-gray-600">
                              {getCreditTypeLabel(session.lead.creditType)}
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-sm text-gray-600">
                              {formatCurrency(session.lead.amount)}
                            </span>
                          </>
                        )}
                        <span className="text-gray-300">•</span>
                        <span className="text-sm text-gray-500">
                          {formatDateTime(session.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <Badge variant={getStatusVariant(session.status)}>
                      {getStatusLabel(session.status)}
                    </Badge>
                    <Link to={`/sessions/${session.id}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        rightIcon={<ArrowRight className="w-4 h-4" />}
                      >
                        Szczegóły
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Session info */}
                {session.offers && session.offers.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600">
                      {session.offers.length} {session.offers.length === 1 ? "oferta" : "ofert"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
